import { useCallback, useRef, useState } from 'react';
import {
  DEFAULT_STOREFRONT_OBJECT_POSITION,
  isDefaultStorefrontObjectPosition,
  sanitizeStorefrontObjectPosition,
} from './storefrontObjectPosition';

/**
 * Aperçu drag du cadrage bandeau (même viewport cover que Website).
 * Pointer Events = souris + tactile.
 */
export default function StorefrontFocalAdjuster({
  imageUrl,
  position,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const frameRef = useRef(null);
  const dragRef = useRef(null);
  const pos = sanitizeStorefrontObjectPosition(position);

  const commit = useCallback(
    (next) => {
      onChange?.(sanitizeStorefrontObjectPosition(next));
    },
    [onChange]
  );

  const onPointerDown = (e) => {
    if (!frameRef.current) return;
    e.preventDefault();
    const rect = frameRef.current.getBoundingClientRect();
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: pos.x,
      originY: pos.y,
      width: rect.width,
      height: rect.height,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    // Déplacer l’image : drag droite → zone visible se décale à gauche (object-position ↓)
    const dx = ((e.clientX - drag.startX) / Math.max(1, drag.width)) * 100;
    const dy = ((e.clientY - drag.startY) / Math.max(1, drag.height)) * 100;
    commit({
      x: drag.originX - dx,
      y: drag.originY - dy,
    });
  };

  const onPointerUp = (e) => {
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
  };

  if (!imageUrl) return null;

  return (
    <div className="mt-3" data-testid="storefront-focal-adjuster">
      {!open ? (
        <button
          type="button"
          className="text-sm font-medium text-primary-700 hover:text-primary-800"
          onClick={() => setOpen(true)}
          data-testid="storefront-focal-open"
        >
          Ajuster le cadrage du bandeau
        </button>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-gray-800">Cadrage du bandeau</p>
            <button
              type="button"
              className="text-xs text-gray-500 hover:text-gray-700"
              onClick={() => setOpen(false)}
            >
              Masquer
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Déplacez l’image pour choisir la zone visible
          </p>
          <div
            ref={frameRef}
            className="relative w-full h-56 sm:h-56 rounded-xl overflow-hidden border border-white/20 shadow-sm cursor-grab active:cursor-grabbing touch-none select-none bg-gray-900"
            data-testid="storefront-focal-preview"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            role="presentation"
          >
            <img
              src={imageUrl}
              alt=""
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ objectPosition: `${pos.x}% ${pos.y}%` }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'linear-gradient(180deg, rgba(74,20,140,0) 0%, rgba(74,20,140,0) 38%, rgba(123,31,162,0.26) 62%, rgba(123,31,162,0.56) 82%, rgba(233,30,99,0.75) 100%)',
              }}
              aria-hidden
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="text-xs font-medium text-gray-700 hover:text-gray-900 underline-offset-2 hover:underline"
              data-testid="storefront-focal-reset"
              onClick={() => commit({ ...DEFAULT_STOREFRONT_OBJECT_POSITION })}
              disabled={isDefaultStorefrontObjectPosition(pos)}
            >
              Réinitialiser le cadrage
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
